import { useState } from 'react';
import { useAppStore } from '../state/store';

export function LoginScreen() {
  const { login, submitTotp, loading, error, pendingChallenge, clearError } = useAppStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');

  return (
    <div
      className="relative z-10 flex h-full w-full items-center justify-center"
      style={{ background: 'radial-gradient(ellipse at center, rgba(0,20,5,0.8) 0%, rgba(0,6,0,1) 100%)' }}
    >
      {/* Grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,255,65,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,65,0.12) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Scan bar */}
      <div className="term-scan-bar pointer-events-none" />

      <div
        className="relative w-[22rem] p-0 z-10"
        style={{
          border: '1px solid rgba(0,255,65,0.5)',
          background: 'rgba(0,8,2,0.97)',
          boxShadow: '0 0 30px rgba(0,255,65,0.12), 0 0 80px rgba(0,255,65,0.05), inset 0 0 30px rgba(0,255,65,0.02)',
        }}
      >
        {/* Corner brackets */}
        <div className="pointer-events-none absolute top-0 left-0 w-4 h-4 border-t border-l border-neon-green" style={{ boxShadow: '0 0 6px rgba(0,255,65,0.4)' }} />
        <div className="pointer-events-none absolute top-0 right-0 w-4 h-4 border-t border-r border-neon-green" style={{ boxShadow: '0 0 6px rgba(0,255,65,0.4)' }} />
        <div className="pointer-events-none absolute bottom-0 left-0 w-4 h-4 border-b border-l border-neon-green" style={{ boxShadow: '0 0 6px rgba(0,255,65,0.4)' }} />
        <div className="pointer-events-none absolute bottom-0 right-0 w-4 h-4 border-b border-r border-neon-green" style={{ boxShadow: '0 0 6px rgba(0,255,65,0.4)' }} />

        {/* Header bar */}
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{
            borderBottom: '1px solid rgba(0,255,65,0.25)',
            background: 'rgba(0,255,65,0.04)',
          }}
        >
          <span className="font-mono text-[9px] tracking-[0.3em] text-neon-green/50 uppercase">
            SYSTEM·AUTHENTICATION
          </span>
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-neon-red" />
            <span className="h-1.5 w-1.5 rounded-full bg-neon-amber" />
            <span className="h-1.5 w-1.5 rounded-full bg-neon-green animate-pulse" />
          </div>
        </div>

        {/* Main content */}
        <div className="px-6 py-5">
          {/* Title block */}
          <div className="mb-6 text-center">
            <div
              className="font-mono text-xl tracking-[0.35em] text-neon-green uppercase font-bold"
              style={{ textShadow: '0 0 12px rgba(0,255,65,0.8), 0 0 30px rgba(0,255,65,0.3)' }}
            >
              THE HORIZON
            </div>
            <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-neon-gr">
              ─── CAPTAIN&apos;S TERMINAL ───
            </div>
            <div className="mt-2 font-mono text-[8px] text-neon-green/25 tracking-wider">
              OPS·STATION·ALPHA&nbsp;&nbsp;[SECURE·CHANNEL]
            </div>
          </div>

          {/* Boot messages */}
          <div
            className="mb-4 px-3 py-2 font-mono text-[9px] text-neon-green/45 space-y-0.5"
            style={{ borderLeft: '2px solid rgba(0,255,65,0.2)', background: 'rgba(0,255,65,0.02)' }}
          >
            <div>&gt; Initializing secure session...</div>
            <div>&gt; eBay channel: <span className="text-neon-green/70">READY</span></div>
            <div>&gt; Etsy channel: <span className="text-neon-green/70">READY</span></div>
            <div>&gt; Awaiting captain authentication<span className="term-cursor" /></div>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 px-3 py-2 font-mono text-[10px] text-neon-red"
              style={{ border: '1px solid rgba(255,48,48,0.4)', background: 'rgba(255,48,48,0.06)' }}
            >
              <div className="flex items-center justify-between">
                <span>&gt; ERROR: {error}</span>
                <button
                  onClick={clearError}
                  className="text-neon-red/60 hover:text-neon-red ml-2"
                >
                  [×]
                </button>
              </div>
            </div>
          )}

          {/* TOTP form */}
          {pendingChallenge ? (
            <form
              onSubmit={(e) => { e.preventDefault(); submitTotp(totp); }}
              className="space-y-3"
            >
              <div>
                <label className="block font-mono text-[9px] uppercase tracking-wider text-neon-green/50 mb-1.5">
                  &gt; 2FA·VERIFICATION·CODE
                </label>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-3 py-2.5 font-mono text-xl text-center tracking-[0.5em] text-neon-green term-input"
                  style={{ letterSpacing: '0.5em' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || totp.length !== 6}
                className="w-full py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-green border border-neon-green/50 bg-neon-green/8 hover:bg-neon-green/15 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                style={{ boxShadow: loading ? 'none' : '0 0 10px rgba(0,255,65,0.2)' }}
              >
                {loading ? '> VERIFYING...' : '> VERIFY·CODE'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); login(username, password); }}
              className="space-y-3"
            >
              <div>
                <label className="block font-mono text-[9px] uppercase tracking-wider text-neon-green/50 mb-1.5">
                  &gt; CAPTAIN·ID
                </label>
                <input
                  autoFocus
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 font-mono text-sm term-input"
                />
              </div>
              <div>
                <label className="block font-mono text-[9px] uppercase tracking-wider text-neon-green/50 mb-1.5">
                  &gt; ACCESS·PASSPHRASE
                </label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 font-mono text-sm term-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !username || !password}
                className="w-full py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] text-neon-green border border-neon-green/50 bg-neon-green/8 hover:bg-neon-green/15 disabled:opacity-30 disabled:cursor-not-allowed transition-all mt-1"
                style={{ boxShadow: (!loading && username && password) ? '0 0 12px rgba(0,255,65,0.2)' : 'none' }}
              >
                {loading ? '> BOARDING...' : '> BOARD·THE·HORIZON'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-4 py-2 flex items-center justify-between"
          style={{ borderTop: '1px solid rgba(0,255,65,0.15)', background: 'rgba(0,255,65,0.02)' }}
        >
          <span className="font-mono text-[8px] uppercase tracking-widest text-neon-green/25">
            TIER·1 // EBAY
          </span>
          <span className="font-mono text-[8px] uppercase tracking-widest text-neon-green/25">
            ENCRYPTED
          </span>
        </div>
      </div>
    </div>
  );
}
